import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, inject, input, OnDestroy, OnInit, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';

import { Subscription } from 'rxjs';
import { UserNamePipe } from 'src/app/pipes/user-name.pipe';
import { AzureIdStatusPipe } from 'src/app/pipes/azure-id-status.pipe';
import { ExportService } from 'src/app/services/exportService';
import { FileUtilsService } from 'src/app/services/file-utils.service';

import { Auditor } from '../../../models/auditor';
import { Categories } from '../../../models/categories';
import { RecommendationShort } from '../../../models/recommendation/recommendation';
import { Templates } from '../../../models/templates';
import { AccessService } from '../../../services/access.service';
import { ExcelexportService } from '../../../services/excelexport.service';
import { KeepFilterService } from '../../../services/keep-filter.service';
import { RecommendationService } from '../../../services/recommendation.service';

@Component({
  selector: 'app-recommendation-table',
  templateUrl: './recommendation-table.component.html',
  styleUrls: ['./recommendation-table.component.scss'],
  imports: [
    MatToolbar,
    MatProgressSpinner,
    MatButton,
    MatGridList,
    MatGridTile,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatSelect,
    MatOption,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatIcon,
    MatTooltip,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    DatePipe,
    UserNamePipe,
    AzureIdStatusPipe,
    AsyncPipe
  ]
})
export class RecommendationTableComponent implements OnInit, OnDestroy {
  private recommendationService = inject(RecommendationService);
  private keepFilterService = inject(KeepFilterService);
  private route = inject(ActivatedRoute);
  private excelexportService = inject(ExcelexportService);
  private exportService = inject(ExportService);
  private snackBar = inject(MatSnackBar);
  private accessService = inject(AccessService);
  fileUtilService = inject(FileUtilsService);
  router = inject(Router);

  readonly all = input<boolean>(false);
  area!: string;
  lastArea!: string;
  categories: string[] = [];
  titleSelect = '';
  statusSelect = '';
  assessmentSelect = '';
  legalUnitSelect = '';
  plantSelect = '';
  bgSelect = '';
  categorySelect = '';
  download = false;
  recSub!: Subscription;
  possibleCoordinators: string[] = [];
  possibleLegalUnits: string[] = [];
  possiblePlants: string[] = [];
  possibleBgs: string[] = [];

  isError = false;

  displayedColumns: string[] = [
    'status',
    'auditDefinition',
    'assessmentCoordinator',
    'legalUnit',
    'plant',
    'department',
    'settledDateFinally',
    'date_settled',
    'currentDeadlineDate',
    'title',
    'category',
    'remark'
  ];

  dataSource!: MatTableDataSource<RecommendationShort>;
  unfilteredDataSource!: MatTableDataSource<RecommendationShort>;

  readonly paginator = viewChild.required(MatPaginator);
  readonly sort = viewChild.required(MatSort);

  ngOnInit() {
    this.accessService.initRoles();
    this.route.params?.subscribe((param) => {
      this.area = param.area;
      this.getRecommendation();
    });
    this.revertFilter();
  }

  keepFilter() {
    this.keepFilterService.assessmentCoordinator = this.assessmentSelect;
    this.keepFilterService.title = this.titleSelect;
    this.keepFilterService.status = this.statusSelect;
    this.keepFilterService.legalUnit = this.legalUnitSelect;
    this.keepFilterService.plant = this.plantSelect;
    this.keepFilterService.bg = this.bgSelect;
    this.keepFilterService.category = this.categorySelect;
  }

  revertFilter() {
    this.assessmentSelect = this.keepFilterService.assessmentCoordinator;
    this.titleSelect = this.keepFilterService.title;
    this.statusSelect = this.keepFilterService.status;
    this.legalUnitSelect = this.keepFilterService.legalUnit;
    this.plantSelect = this.keepFilterService.plant;
    this.bgSelect = this.keepFilterService.bg;
    this.categorySelect = this.keepFilterService.category;
  }

  isDefined(): boolean {
    if (this.isError) {
      return true;
    }
    if (this.unfilteredDataSource) {
      if (this.unfilteredDataSource.data.length > 0) {
        if (this.lastArea === this.route.snapshot.params?.area) {
          return true;
        }
      } else if (this.lastArea === this.route.snapshot.params?.area) {
        return true;
      }
    }
    return false;
  }

  getRecommendation() {
    if (this.recSub) {
      this.recSub.unsubscribe();
    }
    const area = this.route?.snapshot?.params?.area;
    this.recSub = this.recommendationService.getData(area, this.all()).subscribe(
      (value) => {
        this.dataSource = new MatTableDataSource(value);
        this.unfilteredDataSource = new MatTableDataSource(value);
        this.lastArea = area;
        this.dataSource.paginator = this.paginator();
        this.dataSource.sort = this.sort();
        this.getPossibleKeywordValues();
        this.cleanExcelPayload(this.dataSource.data);
        this.filterData();
      },
      (err) => {
        this.isError = true;
        console.error(err);
        this.showSnackbar();
      }
    );
    switch (this.area) {
      case Auditor.GPIA:
        this.categories = Categories.GPIA;
        break;
      case Auditor.GPRM:
        this.categories = Categories.GPRM;
    }
  }

  showSnackbar() {
    this.snackBar.open('Could not get Recommendations', undefined, {
      panelClass: 'error',
      duration: 2000
    });
  }

  showAll() {
    this.statusSelect = '';
    this.legalUnitSelect = '';
    this.assessmentSelect = '';
    this.titleSelect = '';
    this.plantSelect = '';
    this.bgSelect = '';
    this.categorySelect = '';
    this.dataSource.data = this.unfilteredDataSource.data;
  }

  getPossibleKeywordValues() {
    this.possibleCoordinators = [];
    this.possibleLegalUnits = [];

    this.unfilteredDataSource.data.forEach((element) => {
      if (this.possibleLegalUnits.includes(element.legalUnit) === false && element.legalUnit) {
        this.possibleLegalUnits.push(element.legalUnit);
        this.possibleLegalUnits.sort();
      }
      const assessmentCoordinatorName = element.assessmentCoordinatorName || element.assessmentCoordinator;
      if (assessmentCoordinatorName && !this.possibleCoordinators.includes(assessmentCoordinatorName)) {
        this.possibleCoordinators.push(assessmentCoordinatorName);
        this.possibleCoordinators.sort();
      }
      if (this.possiblePlants.includes(element.plant) === false && element.plant) {
        this.possiblePlants.push(element.plant);
        this.possiblePlants.sort();
      }
      if (this.possibleBgs.includes(element.department) === false && element.department) {
        this.possibleBgs.push(element.department);
        this.possibleBgs.sort();
      }
    });
  }

  filterData() {
    const data = this.unfilteredDataSource.data
      .filter((element) => {
        if (this.titleSelect === '') {
          return true;
        }
        if (!element.title) {
          return false;
        }
        return element.title.toLowerCase().includes(this.titleSelect.toLowerCase());
      })
      .filter((element) => {
        return element.legalUnit?.includes(this.legalUnitSelect) || this.legalUnitSelect === '';
      })
      .filter((element) => {
        const assessmentCoordinatorName = element.assessmentCoordinatorName || element.assessmentCoordinator;
        return assessmentCoordinatorName?.includes(this.assessmentSelect) || this.assessmentSelect === '';
      })
      .filter((element) => {
        return element.status?.includes(this.statusSelect) || this.statusSelect === '';
      })
      .filter((element) => {
        if (this.plantSelect && !element.plant) {
          return false;
        }
        return this.plantSelect === '' || element.plant.includes(this.plantSelect);
      })
      .filter((element) => {
        if (this.bgSelect && !element.department) {
          return false;
        }
        return this.bgSelect === '' || element.department.includes(this.bgSelect);
      })
      .filter((element) => {
        if (this.categorySelect && !element.category) {
          return false;
        }
        return this.categorySelect === '' || element.category.includes(this.categorySelect);
      });
    this.dataSource.data = data;
  }

  isDownload(): boolean {
    return this.download;
  }

  exportExcel() {
    this.download = true;
    const cleanData = this.cleanExcelPayload(this.dataSource.data);
    this.excelexportService
      .exportListRecommendation({ list: cleanData, template: Templates.RECOMMENDATON })
      .subscribe((blob: Blob) => {
        this.exportService.createFileDownloadFromBlob(blob, 'Recommendation.xlsx');
        this.download = false;
      });
  }

  cleanExcelPayload(data: RecommendationShort[]): RecommendationShort[] {
    // It's smarter to take the initial deadline instead of 0
    return data.map((element) => {
      if (!element.currentDeadlineDate) {
        if (element.initialDeadline) {
          element.currentDeadlineDate = element.initialDeadline;
        } else {
          element.currentDeadlineDate = '';
        }
      }
      if (element.adjournedDate) {
        element.settledDateFinally = element.adjournedDate;
      }
      return element;
    });
  }

  ngOnDestroy() {
    this.keepFilter();
  }

  navigateToRecommendation(entry: RecommendationShort): void {
    this.router.navigateByUrl(`/recommendation_detail/${entry.id}`);
  }
}
